import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import { apiGet, apiPut, apiPost, apiDelete } from '../utils/apiUtils';
import AssetsCategory from './AssetsCategory';
import MusicCategory from './MusicCategory';
import StatesCategory from './StatesCategory';
import WorldPromptsCategory from './WorldPromptsCategory';
import CharactersCategory from './CharactersCategory';
import InteractiveScenesCategory from './InteractiveScenesCategory';
import StaticScenesCategory from './StaticScenesCategory';
import OpenWorldScenesCategory from './OpenWorldScenesCategory';
import NovelGraphView from './NovelGraphView';
import ParticleBackground from './ParticleBackground';
import { getAllScenes } from '../utils/novelUtils';
import { IS_EUROPE } from '../config/region';
import './BuildingNovelDetail.css';
import './Building.css';
import CustomSelect from './CustomSelect';

// The language the author writes this novel in. A player reading in the same language gets the
// author's own words; every other language is translated on demand and cached by the backend.
// Value strings must match the backend's SUPPORTED_LANGUAGES exactly.
const SOURCE_LANGUAGES = [
  { value: 'russian', label: 'Русский' },
  { value: 'english', label: 'English' },
  { value: 'german',  label: 'Deutsch' },
  { value: 'french',  label: 'Français' },
  { value: 'spanish', label: 'Español' },
  { value: 'italian', label: 'Italiano' },
  { value: 'polish',  label: 'Polski' },
];

const sourceLanguageLabel = (value) =>
  SOURCE_LANGUAGES.find((l) => l.value === value)?.label || 'Русский';

const CATEGORIES = [
  { id: 'info',             label: 'Информация',          icon: 'ℹ️' },
  { id: 'states',           label: 'Состояния',           icon: '🎛️' },
  { id: 'worldPrompts',     label: 'Промпты мира',        icon: '🌍' },
  { id: 'characters',       label: 'Персонажи',           icon: '👥' },
  { id: 'music',            label: 'Музыка',              icon: '🎵' },
  { id: 'assets',           label: 'Ресурсы',             icon: '🖼️' },
  { id: 'graph',            label: 'Граф',                icon: '📊' },
  { id: 'interactiveScenes',label: 'Интерактивные сцены', icon: '🎮' },
  { id: 'staticScenes',     label: 'Статические сцены',   icon: '📖' },
  { id: 'openWorldScenes',  label: 'Открытый мир',         icon: '🌐' }
];

// Initial category from ?category= (e.g. when returning from a scene detail page), else 'info'
const getInitialCategory = () => {
  const cat = new URLSearchParams(window.location.search).get('category');
  return CATEGORIES.some(c => c.id === cat) ? cat : 'info';
};

const BuildingNovelDetail = () => {
  const { novelId: urlNovelId } = useParams();
  const navigate = useNavigate();
  const isProduction = window.location.pathname.includes('production_change');

  const [novel, setNovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoFormData, setInfoFormData] = useState({});
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [playLink, setPlayLink] = useState('');
  const [publishError, setPublishError] = useState(null);
  const [publishLoading, setPublishLoading] = useState(false);

  // For production, the real MongoDB ID comes from the fetched novel
  const novelId = isProduction ? (novel?.id || urlNovelId) : urlNovelId;

  useEffect(() => {
    fetchNovel();
  }, [urlNovelId]);

  const fetchNovel = async () => {
    try {
      setLoading(true);
      if (isProduction) {
        const response = await apiGet(API_URLS.PRODUCTION_NOVEL_BY_ID(urlNovelId), {}, false, '', true);
        // Only owners and admins receive full_novel; everyone else can't edit.
        if (!response.data.full_novel) {
          setError('Доступ запрещён: у вас нет прав для редактирования этой новеллы');
          return;
        }
        setNovel({
          ...response.data.full_novel,
          id: response.data.full_novel.id || response.data.full_novel._id
        });
      } else {
        const response = await apiGet(`${API_URLS.BUILDING_NOVELS}/${urlNovelId}`, {}, false, '', true);
        setNovel(response.data);
      }
    } catch (err) {
      if (err.message && err.message.includes('Доступ к конструктору закрыт')) {
        setAccessDenied(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const backHref = isProduction ? '/search' : '/building';

  const handleDeleteNovel = async () => {
    try {
      setLoading(true);
      if (isProduction) {
        await apiDelete(`${API_URLS.PRODUCTION_NOVELS}/${novelId}`);
        navigate('/search');
      } else {
        await apiDelete(`${API_URLS.BUILDING_NOVELS}/${urlNovelId}`);
        navigate('/building');
      }
    } catch (err) {
      console.error('Error deleting novel:', err);
      setError(err.message || 'Ошибка удаления новеллы');
    } finally {
      setLoading(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleClosePublishModal = () => {
    setShowPublishModal(false);
    setPlayLink('');
    setPublishError(null);
  };

  // Set of scene ids reachable from the first scene, following every ending transition.
  // Scene types are lowercase ("static" | "interactive" | "open_world"), matching the
  // backend SceneType enum. Mirrors NovelGraphView's edge logic; looks the type up by id
  // (scene ids are globally unique) so it never depends on the casing of ref.scene_type.
  const getReachableSceneIds = () => {
    if (!novel?.first_scene_id) return new Set();

    const byId = new Map();
    novel.interactive_scenes?.forEach(s => byId.set(s.id, { scene: s, type: 'interactive' }));
    novel.static_scenes?.forEach(s => byId.set(s.id, { scene: s, type: 'static' }));
    novel.open_world_interactive_scenes?.forEach(s => byId.set(s.id, { scene: s, type: 'open_world' }));

    const visited = new Set();
    const toVisit = [novel.first_scene_id];
    while (toVisit.length > 0) {
      const id = toVisit.pop();
      if (visited.has(id)) continue;
      visited.add(id);

      const entry = byId.get(id);
      if (!entry) continue;
      const { scene, type } = entry;

      const nextIds = [];
      if (type === 'static') {
        scene.next_scenes?.forEach(ns => { if (ns.scene_id) nextIds.push(ns.scene_id); });
      } else {
        // interactive & open_world both branch through endings + static_endings
        scene.endings?.forEach(e => { if (e.next_scene?.scene_id) nextIds.push(e.next_scene.scene_id); });
        scene.static_endings?.forEach(se => { if (se.next_scene?.scene_id) nextIds.push(se.next_scene.scene_id); });
        if (type === 'interactive' && scene.context_ending?.next_scene?.scene_id) {
          nextIds.push(scene.context_ending.next_scene.scene_id);
        }
      }
      nextIds.forEach(nid => { if (!visited.has(nid)) toVisit.push(nid); });
    }
    return visited;
  };

  const validateInteractiveScenes = () => {
    if (!novel?.interactive_scenes) return { isValid: true, invalidScenes: [] };
    const reachableSceneIds = getReachableSceneIds();
    const invalidScenes = [];

    novel.interactive_scenes.forEach(scene => {
      if (!reachableSceneIds.has(scene.id)) return;
      if (scene.interactive === false) return;
      if (!scene.characters || scene.characters.length === 0) {
        invalidScenes.push(scene.name);
        return;
      }
      const hasActiveCharacter = scene.characters.some(c => c.inactive === false || c.inactive === undefined);
      if (!hasActiveCharacter) invalidScenes.push(scene.name);
    });

    return { isValid: invalidScenes.length === 0, invalidScenes };
  };

  const validateScenePrompts = () => {
    const overLimitScenes = [];
    const allScenes = [
      ...(novel?.interactive_scenes || []),
      ...(novel?.open_world_interactive_scenes || []),
    ];
    allScenes.forEach(scene => {
      const total = (scene.additional_prompt_descriptions || [])
        .reduce((sum, p) => sum + (p.prompt_description || '').length, 0);
      if (total > 10000) overLimitScenes.push(`${scene.name} (${total} симв.)`);
    });
    return overLimitScenes;
  };

  const validatePlayLink = () => {
    if (!playLink.trim()) {
      setPublishError('Ссылка для игры обязательна');
      return false;
    }
    if (!/^[a-z0-9_-]+$/.test(playLink)) {
      setPublishError('Ссылка должна содержать только строчные буквы, цифры, дефисы или подчеркивания');
      return false;
    }
    return true;
  };

  const handlePublish = async () => {
    if (!validatePlayLink()) return;
    const sceneValidation = validateInteractiveScenes();
    if (!sceneValidation.isValid) {
      setPublishError('Следующие интерактивные сцены не имеют активных персонажей и не могут быть опубликованы:\n• ' + sceneValidation.invalidScenes.join('\n• '));
      return;
    }
    const overLimitScenes = validateScenePrompts();
    if (overLimitScenes.length > 0) {
      setPublishError('Следующие сцены превышают лимит промптов сцены в 10 000 символов:\n• ' + overLimitScenes.join('\n• '));
      return;
    }
    try {
      setPublishLoading(true);
      setPublishError(null);
      await apiPost(`${API_URLS.BUILDING_NOVELS}/${urlNovelId}/production_novel`, { play_link: playLink });
      setShowPublishModal(false);
      navigate(`/production/${playLink}`);
    } catch (err) {
      setPublishError(err.response?.status === 400 ? 'Эта ссылка уже занята' : err.message);
    } finally {
      setPublishLoading(false);
    }
  };

  const handleEditInfo = () => {
    setInfoFormData({
      name: novel?.name || '',
      description: novel?.description || '',
      source_language: novel?.source_language || 'russian',
      required_18_age: novel?.required_18_age || false,
      first_scene_id: novel?.first_scene_id || '',
      first_scene_type: novel?.first_scene_type || '',
      cover_background_image_id: novel?.cover_background_image_id || ''
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!infoFormData.name?.trim()) {
      setError('Название новеллы обязательно');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const updatedNovel = {
        ...novel,
        name: infoFormData.name.trim(),
        description: infoFormData.description.trim(),
        source_language: infoFormData.source_language || 'russian',
        required_18_age: infoFormData.required_18_age,
        first_scene_id: infoFormData.first_scene_id || null,
        first_scene_type: infoFormData.first_scene_type || null,
        cover_background_image_id: infoFormData.cover_background_image_id || null
      };
      const url = isProduction
        ? `${API_URLS.PRODUCTION_NOVELS}/${novelId}`
        : `${API_URLS.BUILDING_NOVELS}/${urlNovelId}`;
      await apiPut(url, updatedNovel, {}, true, 'Информация о новелле успешно обновлена!');
      await fetchNovel();
      setEditingInfo(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingInfo(false);
    setInfoFormData({});
    setError(null);
  };

  const getCoverImageUrl = () => {
    if (!novel?.cover_background_image_id) return null;
    const bg = (novel?.assets?.backgrounds || []).find(b => b.id === novel.cover_background_image_id);
    if (!bg) return null;
    const prefix = isProduction ? 'production' : 'building';
    const id = isProduction ? novelId : urlNovelId;
    const url = `${GCS_BUCKET_URL}/${prefix}/${id}/background/${novel.cover_background_image_id}.${bg.extension}`;
    return bg.cache_id ? `${url}?cache_id=${bg.cache_id}` : url;
  };

  /*
   * Russia only. The screen recruits authors into the constructor through a Yandex form, in
   * Russian, and the form does not accept European applicants — Plotex does not run the open
   * author intake that AI Novel does, which is why /authors is not routed here either. A visitor
   * without creator access gets an empty page rather than an invitation they cannot act on.
   */
  if (accessDenied) {
    if (IS_EUROPE) return null;
    return (
      <div className="bnd-wrap">
        <ParticleBackground />
        <div className="bnd-app">
          <div className="bnd-loading">
            <h2 style={{ color: '#e2e8f0', marginBottom: '1rem' }}>Доступ закрыт</h2>
            <div className="bl-empty">
              <p>Чтобы получить доступ к конструктору, заполните форму:</p>
              <a
                href="https://forms.yandex.ru/cloud/6a36840cd046882832495672/"
                target="_blank"
                rel="noopener noreferrer"
                className="bl-add-btn"
                style={{
                  background: 'linear-gradient(135deg, #7B2FFF, #a78bfa)',
                  color: '#fff',
                  textDecoration: 'none',
                  marginTop: '12px'
                }}
              >
                📝 Заполнить форму
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bnd-wrap">
        <ParticleBackground />
        <div className="bnd-app">
          <div className="bnd-loading">
            <div className="bnd-spinner" />
            <span>Загрузка...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bnd-wrap">
      <ParticleBackground />
      <div className="bnd-app">

        {isProduction && (
          <div className="bnd-prod-warning">
            ⚠️ Продакшн новелла — будьте осторожны при редактировании ⚠️
          </div>
        )}

        <div className="bnd-header">
          <a href={backHref} className="bnd-back">
            {isProduction ? '← Поиск' : '← Созданиe'}
          </a>
          <h1 className="bnd-title">{novel?.name || 'Новелла'}</h1>
          <div className="bnd-header-actions">
            {!isProduction && (
              <button className="bnd-btn-primary" onClick={() => setShowPublishModal(true)}>
                Опубликовать
              </button>
            )}
          </div>
        </div>

        <CustomSelect
          className="bnd-cat-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
          ))}
        </CustomSelect>

        <div className="bnd-body">
          <nav className="bnd-sidebar">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`bnd-sidebar-btn${selectedCategory === cat.id ? ' active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                <span className="bnd-sidebar-icon">{cat.icon}</span>
                <span className="bnd-sidebar-label">{cat.label}</span>
              </button>
            ))}
          </nav>

          <div className="bnd-main">
            {selectedCategory === 'info' ? (
              <div>
                {error && <div className="bnd-error-inline">{error}</div>}

                {editingInfo ? (
                  <div>
                    <div className="bnd-form-group">
                      <label className="bnd-form-label">Название новеллы</label>
                      <input
                        type="text"
                        value={infoFormData.name}
                        onChange={(e) => setInfoFormData({ ...infoFormData, name: e.target.value })}
                        className="bnd-input"
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="bnd-form-group">
                      <label className="bnd-form-label">Описание</label>
                      <textarea
                        value={infoFormData.description}
                        onChange={(e) => setInfoFormData({ ...infoFormData, description: e.target.value })}
                        className="bnd-textarea"
                        disabled={loading}
                        rows={3}
                      />
                    </div>

                    <div className="bnd-form-group">
                      <label className="bnd-form-label">Язык оригинала</label>
                      <CustomSelect
                        value={infoFormData.source_language}
                        onChange={(e) => setInfoFormData({ ...infoFormData, source_language: e.target.value })}
                        className="app-select"
                        disabled={loading}
                      >
                        {SOURCE_LANGUAGES.map((l) => (
                          <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                      </CustomSelect>
                      <p className="bnd-form-help">
                        Язык, на котором написана новелла. Игроки, читающие на этом языке, увидят
                        ваш текст как есть — без перевода. Для остальных языков текст переводится
                        автоматически.
                      </p>
                    </div>

                    <div className="bnd-form-group">
                      <label className="bnd-checkbox-label">
                        <input
                          type="checkbox"
                          checked={infoFormData.required_18_age}
                          onChange={(e) => setInfoFormData({ ...infoFormData, required_18_age: e.target.checked })}
                          disabled={loading}
                          className="bnd-checkbox"
                        />
                        <span>Требуется подтверждение возраста 18+</span>
                      </label>
                    </div>

                    <div className="bnd-form-group">
                      <label className="bnd-form-label">Первая сцена</label>
                      <CustomSelect
                        value={infoFormData.first_scene_id}
                        onChange={(e) => {
                          const s = getAllScenes(novel).find(sc => sc.id === e.target.value);
                          setInfoFormData({
                            ...infoFormData,
                            first_scene_id: e.target.value,
                            first_scene_type: s ? s.type : ''
                          });
                        }}
                        className="app-select"
                        disabled={loading}
                      >
                        <option value="">Выберите первую сцену</option>
                        {getAllScenes(novel).map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.type})</option>
                        ))}
                      </CustomSelect>
                      {getAllScenes(novel).length === 0 && (
                        <p className="bnd-form-help">Нет доступных сцен</p>
                      )}
                    </div>

                    <div className="bnd-form-group">
                      <label className="bnd-form-label">Фоновое изображение обложки</label>
                      <CustomSelect
                        value={infoFormData.cover_background_image_id}
                        onChange={(e) => setInfoFormData({ ...infoFormData, cover_background_image_id: e.target.value })}
                        className="app-select"
                        disabled={loading}
                      >
                        <option value="">Выберите фоновое изображение</option>
                        {(novel?.assets?.backgrounds || []).map(bg => (
                          <option key={bg.id} value={bg.id}>{bg.name}</option>
                        ))}
                      </CustomSelect>
                      {(novel?.assets?.backgrounds || []).length === 0 && (
                        <p className="bnd-form-help">Нет доступных фоновых изображений</p>
                      )}
                    </div>

                    <div className="bnd-form-actions">
                      <button type="button" onClick={handleSaveInfo} disabled={loading} className="bnd-btn-primary">
                        {loading ? 'Загрузка...' : 'Сохранить'}
                      </button>
                      <button type="button" onClick={handleCancelEdit} disabled={loading} className="bnd-btn-secondary">
                        Отменить
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bnd-info-card">
                    <div className="bnd-info-row">
                      <span className="bnd-info-label">Название</span>
                      <span className="bnd-info-value">{novel?.name}</span>
                    </div>
                    <div className="bnd-info-row">
                      <span className="bnd-info-label">Описание</span>
                      <span className="bnd-info-value">{novel?.description || 'Нет описания'}</span>
                    </div>
                    <div className="bnd-info-row">
                      <span className="bnd-info-label">Язык оригинала</span>
                      <span className="bnd-info-value">{sourceLanguageLabel(novel?.source_language)}</span>
                    </div>
                    <div className="bnd-info-row">
                      <span className="bnd-info-label">Возраст 18+</span>
                      <span className="bnd-info-value">{novel?.required_18_age ? 'Да' : 'Нет'}</span>
                    </div>
                    <div className="bnd-info-row">
                      <span className="bnd-info-label">Первая сцена</span>
                      <span className="bnd-info-value">
                        {novel?.first_scene_id
                          ? `${getAllScenes(novel).find(s => s.id === novel.first_scene_id)?.name || 'Неизвестно'} (${novel.first_scene_type})`
                          : 'Не задано'}
                      </span>
                    </div>
                    <div className="bnd-info-row">
                      <span className="bnd-info-label">Обложка</span>
                      <span className="bnd-info-value">
                        {novel?.cover_background_image_id ? (
                          <div>
                            <span>{(novel?.assets?.backgrounds || []).find(bg => bg.id === novel.cover_background_image_id)?.name || 'Неизвестно'}</span>
                            {getCoverImageUrl() && (
                              <div>
                                <img
                                  src={getCoverImageUrl()}
                                  alt="Обложка"
                                  className="bnd-cover-thumb"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }}
                                />
                                <span style={{ display: 'none', color: '#ef4444', fontSize: '0.8rem' }}>Ошибка загрузки</span>
                              </div>
                            )}
                          </div>
                        ) : 'Не задано'}
                      </span>
                    </div>
                    <div className="bnd-info-row">
                      <span className="bnd-info-label">Создана</span>
                      <span className="bnd-info-value">
                        {novel?.created_at ? new Date(novel.created_at).toLocaleDateString('ru-RU') : 'Неизвестно'}
                      </span>
                    </div>
                    <div className="bnd-info-edit-btn">
                      <button className="bnd-btn-secondary" onClick={handleEditInfo} disabled={loading}>
                        Редактировать
                      </button>
                    </div>
                  </div>
                )}
              </div>

            ) : selectedCategory === 'assets' ? (
              <AssetsCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'music' ? (
              <MusicCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'states' ? (
              <StatesCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'worldPrompts' ? (
              <WorldPromptsCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'characters' ? (
              <CharactersCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'interactiveScenes' ? (
              <InteractiveScenesCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'staticScenes' ? (
              <StaticScenesCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'openWorldScenes' ? (
              <OpenWorldScenesCategory novelId={novelId} novel={novel} setNovel={setNovel} refreshNovel={fetchNovel} isProduction={isProduction} />
            ) : selectedCategory === 'graph' ? (
              <NovelGraphView novel={novel} novelId={novelId} isProduction={isProduction} />
            ) : (
              <div className="bnd-loading"><p>Скоро...</p></div>
            )}
          </div>
        </div>
      </div>

      {selectedCategory === 'info' && (
        <div className="bnd-danger-zone">
          <button className="bnd-btn-danger" onClick={() => setShowDeleteConfirmation(true)}>
            Удалить новеллу
          </button>
        </div>
      )}

      {/* Модал публикации (только building) */}
      {showPublishModal && (
        <div className="bnd-overlay">
          <div className="bnd-modal">
            <div className="bnd-modal-head">
              <h3 className="bnd-modal-title">Опубликовать новеллу</h3>
              <button className="bnd-modal-close" onClick={handleClosePublishModal}>×</button>
            </div>
            <div className="bnd-modal-body">
              <div className="bnd-form-group">
                <label className="bnd-form-label" htmlFor="playLink">Ссылка для игры</label>
                <input
                  type="text"
                  id="playLink"
                  value={playLink}
                  onChange={(e) => setPlayLink(e.target.value)}
                  placeholder="например: my-novel"
                  className="bnd-input"
                  disabled={publishLoading}
                />
                <small className="bnd-form-help">Только строчные буквы, цифры, дефисы и подчеркивания</small>
              </div>
              {publishError && <div className="bnd-error-inline">{publishError}</div>}
            </div>
            <div className="bnd-modal-footer">
              <button className="bnd-btn-secondary" onClick={handleClosePublishModal} disabled={publishLoading}>Отмена</button>
              <button className="bnd-btn-primary" onClick={handlePublish} disabled={publishLoading}>
                {publishLoading ? 'Загрузка...' : 'Опубликовать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модал подтверждения удаления */}
      {showDeleteConfirmation && (
        <div className="bnd-overlay">
          <div className="bnd-modal">
            <div className="bnd-modal-head">
              <h3 className="bnd-modal-title">Удалить новеллу</h3>
              <button className="bnd-modal-close" onClick={() => setShowDeleteConfirmation(false)}>×</button>
            </div>
            <div className="bnd-modal-body">
              <p>Вы уверены, что хотите удалить эту новеллу?</p>
              <p>Это действие нельзя отменить. Все сцены и ресурсы будут удалены навсегда.</p>
            </div>
            <div className="bnd-modal-footer">
              <button className="bnd-btn-secondary" onClick={() => setShowDeleteConfirmation(false)} disabled={loading}>Отмена</button>
              <button className="bnd-btn-danger" onClick={handleDeleteNovel} disabled={loading}>
                {loading ? 'Загрузка...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingNovelDetail;
